import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  WritableSignal,
  computed,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router, UrlTree, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { zip } from 'rxjs';
import * as _ from 'lodash';
import { SummaryService } from './summary.service';
import { OnDemandPreloadingStrategy } from '../core/services/on-demand-preloading.service';
import {
  ActiveFilterService,
  ViewService,
  ComparisonMode,
  ITopListItem,
  IListConfig,
  ConfigService,
  ChartType,
  ChartSwitcher,
  RentalGrowthData,
  IRentalGrowthItem,
  RentalGrowthItemType,
  RentalGrowthType,
  MathService,
  ClientProfileService,
  ChartSwitchingService,
  ChartDisplayer,
} from '../core';
import {
  ChartData,
  TopItemsData,
  TxAttributes,
  DashboardCardType,
  DashboardCardSetData,
  FilterDefault,
} from '../models';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { Subscription } from 'rxjs';
import { FilterProfileService } from '../filter/profiles/filter-profile.service';
import { CODENAMES } from '../core/constants';
import { ClientBrandingService } from '@/app/core/client/client-branding.service';
import { normalizePercentNullsToZero } from '@/app/core/utils/normalize-nulls';

enum GEOGRAPHY_LISTS {
  REGIONS = 'tables.regions_districts_branches.regions',
  DISTRICTS = 'tables.regions_districts_branches.districts',
  BRANCHES = 'tables.regions_districts_branches.branches',
}

export function deriveRentedAsState(
  isRentedAsAvailable: boolean,
  sessionGridConfig: string | undefined
): boolean {
  // The default state is true, if the client has rented as data.
  let result = isRentedAsAvailable;

  // if there is rented as data and the user has switched the column order to make it false
  if (isRentedAsAvailable && sessionGridConfig) {
    const gridConfig = JSON.parse(sessionGridConfig);
    const productTypeFind = (column) =>
      column.title === 'main.core.common.counts.product_types.singular';
    const rentedAsFind = (column) =>
      column.title ===
      'main.core.tables.titles.dimensions.rented_as_product_type';
    if (gridConfig[0].columns) {
      const productTypeColumn = gridConfig[0].columns.find(productTypeFind);
      if (productTypeColumn && productTypeColumn.visible) {
        const rentedAsColumnIndex =
          gridConfig[0].columns.findIndex(rentedAsFind);
        const productTypeColumnIndex =
          gridConfig[0].columns.findIndex(productTypeFind);
        if (productTypeColumnIndex < rentedAsColumnIndex) {
          result = false;
        }
      }
    }
  }
  return result;
}

type ChartLoadingState = {
  isLoading: boolean;
  rateChangeComplete: boolean;
  chartsComplete: boolean;
  isComplete: boolean;
};

@Component({
  selector: 'rdo-summary',
  templateUrl: 'summary.component.html',
  styleUrls: ['summary.component.scss', '../../styles/_summary-charts.scss'],
  providers: [MathService],
  standalone: false,
})
export class SummaryComponent implements OnInit, OnDestroy {
  public cardData: DashboardCardSetData;
  public orifChartData: Array<RentalGrowthData> = [] as Array<RentalGrowthData>;
  public hasOrifChartData = false;
  public hasTotalYoYRateChangeData = false;
  public hasTotalMoMRateChangeData = false;
  public hasComparedYoYRateChangeData = false;
  public hasComparedMoMRateChangeData = false;
  public chartData = new ChartData();
  public categories = new TopItemsData();
  public rateTypes = new TopItemsData();
  public geographyTopListConfigs: IListConfig[];
  private subscriptions = [];
  public revenueDistribution: any = [];
  public rateChangeData = [];
  public comparisonMode = ComparisonMode;
  public mode: ComparisonMode = ComparisonMode.Benchmark;
  public chartSwitcher = new ChartSwitcher(null);
  public chartLoadingState: WritableSignal<ChartLoadingState> = signal({
    isLoading: false,
    rateChangeComplete: false,
    chartsComplete: false,
    isComplete: false,
  });
  public showChartLoader = computed(() => {
    const state = this.chartLoadingState();
    return state.isLoading;
  });
  public clientAttributes = new TxAttributes(<TxAttributes>{ IsTrial: true });
  public loading = false;
  weeklyUtilization: any;
  private I18N_PATH = 'main.tabs.summary';
  private i18n = undefined;
  public chartTooltip = '';
  public applyFilterSubscription: Subscription;
  constructor(
    private authenticationService: AuthenticationService,
    private activeroute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private summaryService: SummaryService,
    private filterService: ActiveFilterService,
    private viewService: ViewService,
    private configService: ConfigService,
    private mathService: MathService,
    private translateService: TranslateService,
    private chartSwitchingService: ChartSwitchingService,
    protected clientProfileService: ClientProfileService,
    protected filterProfileService: FilterProfileService,
    protected clientBrandingService: ClientBrandingService,
    private preloadingStrategy: OnDemandPreloadingStrategy
  ) {}

  ngOnInit() {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    this.subscriptions.push(
      zip(
        this.clientProfileService.getClientAttributes(),
        this.filterProfileService.waitForCurrentProfile()
      ).subscribe((results: [TxAttributes, boolean]) => {
        this.clientAttributes = results[0] ?? this.clientAttributes;
        //11/15/2024 ANA-21733 This isnt required as the filterchanged will trigger this.load
        if (!this.loading) {
          this.load();
        }
      })
    );

    this.subscriptions.push(
      this.filterService.filterChange.subscribe(() => {
        this.filterchanged();
      })
    );
  }

  private getGeographyConfigIndex(type: GEOGRAPHY_LISTS) {
    for (let i = 0; i < this.geographyTopListConfigs.length; i++) {
      if (this.geographyTopListConfigs[i].id === type) {
        return i;
      }
    }
  }

  /// <summary>
  /// the filter change will trigger on client toggle but not the load.  it's therefore necessary to perform the same load up
  /// logic (specifically, re-fetching the client attributes) otherwise, these carry over from the prior client
  /// </summary>
  private filterchanged = () => {
    this.subscriptions.push(
      zip(
        this.clientProfileService.getClientAttributes(),
        this.filterProfileService.waitForCurrentProfile()
      ).subscribe((results: [TxAttributes, boolean]) => {
        this.clientAttributes = results[0] ?? this.clientAttributes;
        if (!this.loading) {
          this.load();
        }
      })
    );
  };

  ngOnDestroy() {
    while (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.pop().unsubscribe();
    }
    if (this.applyFilterSubscription) {
      this.applyFilterSubscription.unsubscribe();
    }
  }

  public load = () => {
    this.loading = true;

    const monthsToReturn = this.filterProfileService.readClientProfileProperty(
      CODENAMES.CN_SUMMARY_MONTHS_TO_RETURN
    );
    if (monthsToReturn != null) {
      if (this.applyFilterSubscription) {
        this.applyFilterSubscription.unsubscribe();
      }
      this.applyFilterSubscription = zip(
        this.summaryService.getTopBranches().pipe(
          map((p) =>
            _.map(p.Items, (i: any) => {
              i.Name = i.LocationCode;
              return <ITopListItem>i;
            })
          )
        ),
        this.summaryService
          .getTopCategories()
          .pipe(map((p) => <TopItemsData>{ data: p, loading: false })),
        this.summaryService
          .getRateTypes()
          .pipe(map((p) => <TopItemsData>{ data: p, loading: false })),
        this.summaryService.getCards(),
        this.summaryService.getOnRentAndInFleetData(
          monthsToReturn,
          this.filterService.getCurrentFilter().useRouseSchema
        ),
        this.summaryService.getRevenueDistribution(
          monthsToReturn,
          this.filterService.getCurrentFilter().useRouseSchema
        ),

        this.viewService.getComparisonMode()
        // this.clientBrandingService.load()
      ).subscribe({
        error: (err: unknown) => {
          // Intentional cancellation (filter change mid-flight) surfaces as AbortError.
          // Swallow it silently — a new load() is already in progress.
          // Any other error (real network failure, 5xx, etc.) resets loading
          // so the page doesn't stay stuck, then re-throws for Sentry to capture.
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          this.loading = false;
          throw err;
        },
        next: (
          results: [
            ITopListItem[],
            TopItemsData,
            TopItemsData,
            DashboardCardSetData,
            any,
            any,
            ComparisonMode,
          ]
        ) => {
          this.setupGeographyTopLists(results[0]);
          this.categories = results[1];
          this.rateTypes = results[2];
          this.cardData = results[3];
          this.finishedRentalGrowthChart(results[4]);
          this.revenueDistribution = results[5];
          this.mode = results[6];
          this.updateChartLoadingState({ isLoading: true });
          this.loading = false;
          this.preloadingStrategy.triggerPreload();
          this.summaryService
            .getChartData(
              monthsToReturn,
              this.filterService.getCurrentFilter().useRouseSchema
            )
            .pipe(
              finalize(() => {
                this.updateChartLoadingState({ chartsComplete: true });
              })
            )
            .subscribe((data) => {
              this.chartData.data = data;
              this.summaryService
                .getWeeklyChartData(
                  monthsToReturn,
                  this.filterService.getCurrentFilter().useRouseSchema
                )
                .subscribe((weeklyData) => {
                  this.weeklyUtilization = weeklyData;
                  this.authenticationService.finishSentryTransaction();
                });
            });

          this.chartSwitcher = this.chartSwitchingService.getCurrentChart(
            ChartDisplayer.SUMMARY,
            this.mode,
            this.chartSwitcher,
            null
          );

          this.subscriptions.push(
            this.chartSwitcher.chartChanged.subscribe((chart) => {
              this.chartSwitchingService.setCurrentChart(
                ChartDisplayer.SUMMARY,
                chart
              );
            })
          );

          this.subscriptions.push(
            this.summaryService
              .getRateChangeData(
                monthsToReturn,
                this.filterService.getCurrentFilter().useRouseSchema,
                this.shouldUseRentedAsData()
              )
              .pipe(
                finalize(() => {
                  this.updateChartLoadingState({ rateChangeComplete: true });
                })
              )
              .subscribe((results) => {
                // The new endpoint returns an array, the old one returns an object with Items property.
                this.rateChangeData = normalizePercentNullsToZero(
                  results?.Items ?? results
                );
                this.checkForRateChangeData();
              })
          );
        },
      });
    }
  };

  private updateChartLoadingState(update: Partial<ChartLoadingState>) {
    const currentState = this.chartLoadingState();
    const nextState: ChartLoadingState = { ...currentState, ...update };
    if (
      nextState.isLoading &&
      nextState.chartsComplete &&
      nextState.rateChangeComplete
    ) {
      nextState.isLoading = false;
      nextState.isComplete = true;
    }
    this.chartLoadingState.set(nextState);
  }

  // We have to decide whether to use rented as data or not.

  private shouldUseRentedAsData(): boolean {
    const isRentedAsAvailable = this.filterProfileService.isRentedAsAvailable();
    const sessionGridConfig = window.sessionStorage.getItem(
      'GRID_CFG_PRODUCT_TYPE_CATEGORIES'
    );
    return deriveRentedAsState(isRentedAsAvailable, sessionGridConfig);
  }

  private getCategoryUrlTree = (category): UrlTree => {
    return this.router.createUrlTree([
      'equipment',
      { category: category.RouseCategory },
    ]);
  };

  private checkForRateChangeData = () => {
    this.hasTotalMoMRateChangeData =
      this.hasTotalYoYRateChangeData =
      this.hasComparedMoMRateChangeData =
      this.hasComparedYoYRateChangeData =
        false;
    if (this.rateChangeData) {
      this.rateChangeData.forEach((rateChange) => {
        if (rateChange.RateChangeMoMTotal) {
          this.hasTotalMoMRateChangeData = true;
        }
        if (rateChange.RateChangeYoYTotal) {
          this.hasTotalYoYRateChangeData = true;
        }
        if (rateChange.RateChangeMoMBenchmarked) {
          this.hasComparedMoMRateChangeData = true;
        }
        if (rateChange.RateChangeYoYBenchmarked) {
          this.hasComparedYoYRateChangeData = true;
        }
      });
    }
  };

  public navigateToCategory = (category) => {
    if (this.clientAttributes?.IsTrial) {
      return;
    }
    this.router.navigateByUrl(this.getCategoryUrlTree(category));
  };

  public resolveCategoryUrl = (
    router: Router,
    location: Location,
    item
  ): string => {
    const urlTree = this.getCategoryUrlTree(item);
    return this.location.prepareExternalUrl(urlTree.toString());
  };

  private setupGeographyTopLists = (branches: ITopListItem[]) => {
    this.geographyTopListConfigs = [
      {
        id: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.REGIONS,
        title: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.REGIONS + '.title',
        load: () =>
          this.summaryService.getTopRegions().pipe(
            map((p) =>
              _.map(p.Items, (i: any) => {
                i.Name = i.ClientRegion;
                return <ITopListItem>i;
              })
            )
          ),
        footerLinkDsl: this.clientAttributes?.IsTrial ? null : ['/regions'],
        footerLinkTitle:
          this.I18N_PATH + '.' + GEOGRAPHY_LISTS.REGIONS + '.all',
      },
      {
        id: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.DISTRICTS,
        title: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.DISTRICTS + '.title',
        load: () =>
          this.summaryService.getTopDistricts().pipe(
            map((p) =>
              _.map(p.Items, (i: any) => {
                i.Name = i.ClientDistrict;
                return <ITopListItem>i;
              })
            )
          ),
        footerLinkDsl: this.clientAttributes?.IsTrial ? null : ['/districts'],
        footerLinkTitle:
          this.I18N_PATH + '.' + GEOGRAPHY_LISTS.DISTRICTS + '.all',
      },
      {
        id: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.BRANCHES,
        title: this.I18N_PATH + '.' + GEOGRAPHY_LISTS.BRANCHES + '.title',
        load: () => of(branches),
        footerLinkDsl: this.clientAttributes?.IsTrial ? null : ['/branches'],
        footerLinkTitle:
          this.I18N_PATH + '.' + GEOGRAPHY_LISTS.BRANCHES + '.all',
      },
    ];
  };

  private finishedRentalGrowthChart = (result: any) => {
    this.hasOrifChartData = false;
    if (!result || result.length === 0) {
      this.orifChartData = result;
      return;
    }
    const cd: RentalGrowthData[] = new Array<RentalGrowthData>();
    let i = 0;
    const totalMonths = result.length - 1;
    while (i <= totalMonths) {
      if (!this.hasOrifChartData && result[i].GrowthYoYDiFBenchmarked) {
        this.hasOrifChartData = true;
      }
      // Parse Month as a stable "month bucket" date (UTC noon) to avoid timezone/DST month shifts.
      const [year, month, day] = result[i].Month.split('T')[0]
        .split('-')
        .map(Number);
      const dateFrom = new Date(Date.UTC(year, month - 1, day, 12));
      cd.push(this.getRentalGrowthData(dateFrom, result[i]));
      i++;
    }
    this.orifChartData = cd;
  };

  private getRentalGrowthData(
    startDate: Date,
    currentYearRaw: any
  ): RentalGrowthData {
    if (currentYearRaw) {
      const unitsOnRent: IRentalGrowthItem = {
        Title: 'Units On Rent',
        ItemType: RentalGrowthItemType.unitsOnRent,
        ClientAvg: currentYearRaw.UnitsOnRentAvgYoYBenchmarked,
        ClientGrowth: currentYearRaw.GrowthYoYDoRBenchmarked,
        BenchmarkAvg: null,
        BenchmarkGrowth: currentYearRaw.GrowthYoYDoRBench,
        CvsBGrowthUnitsDiff: currentYearRaw.UnitsOnRentAvgYoYDifference,
        CvsBGrowthPointsDiff:
          currentYearRaw.GrowthYoYDoRBenchmarked != null &&
          currentYearRaw.GrowthYoYDoRBench != null
            ? this.mathService.substract(
                currentYearRaw.GrowthYoYDoRBenchmarked,
                currentYearRaw.GrowthYoYDoRBench,
                1
              )
            : null,
        ClientTotal: currentYearRaw.UnitsOnRentAvgTotal,
        GrowthTotal: currentYearRaw.GrowthYoYDoRTotal,
        // MoM Data
        ClientAvgMoM: currentYearRaw.UnitsOnRentAvgMoMBenchmarked,
        ClientGrowthMoM: currentYearRaw.GrowthMoMDoRBenchmarked,
        BenchmarkGrowthMoM: currentYearRaw.GrowthMoMDoRBench,
        CvsBGrowthUnitsDiffMoM: currentYearRaw.UnitsOnRentAvgMoMDifference,
        CvsBGrowthPointsDiffMoM:
          currentYearRaw.GrowthMoMDoRBenchmarked != null &&
          currentYearRaw.GrowthMoMDoRBench != null
            ? this.mathService.substract(
                currentYearRaw.GrowthMoMDoRBenchmarked,
                currentYearRaw.GrowthMoMDoRBench,
                1
              )
            : null,
        GrowthTotalMoM: currentYearRaw.GrowthMoMDoRTotal,
      };
      const unitsOnFleet: IRentalGrowthItem = {
        Title: 'Units In Fleet',
        ItemType: RentalGrowthItemType.unitsOnFleet,
        ClientAvg: currentYearRaw.UnitsInFleetAvgYoYBenchmarked,
        ClientGrowth: currentYearRaw.GrowthYoYDiFBenchmarked,
        BenchmarkAvg: null,
        BenchmarkGrowth: currentYearRaw.GrowthYoYDiFBench,
        CvsBGrowthUnitsDiff: currentYearRaw.UnitsInFleetAvgYoYDifference,
        CvsBGrowthPointsDiff:
          currentYearRaw.GrowthYoYDiFBenchmarked != null &&
          currentYearRaw.GrowthYoYDiFBench != null
            ? this.mathService.substract(
                currentYearRaw.GrowthYoYDiFBenchmarked,
                currentYearRaw.GrowthYoYDiFBench,
                1
              )
            : null,
        ClientTotal: currentYearRaw.UnitsInFleetAvgTotal,
        GrowthTotal: currentYearRaw.GrowthYoYDiFTotal,
        // MoM Data
        ClientAvgMoM: currentYearRaw.UnitsInFleetAvgMoMBenchmarked,
        ClientGrowthMoM: currentYearRaw.GrowthMoMDiFBenchmarked,
        BenchmarkGrowthMoM: currentYearRaw.GrowthMoMDiFBench,
        CvsBGrowthUnitsDiffMoM: currentYearRaw.UnitsInFleetAvgMoMDifference,
        CvsBGrowthPointsDiffMoM:
          currentYearRaw.GrowthMoMDiFBenchmarked != null &&
          currentYearRaw.GrowthMoMDiFBench != null
            ? this.mathService.substract(
                currentYearRaw.GrowthMoMDiFBenchmarked,
                currentYearRaw.GrowthMoMDiFBench,
                1
              )
            : null,
        GrowthTotalMoM: currentYearRaw.GrowthMoMDiFTotal,
      };
      return new RentalGrowthData(
        startDate,
        RentalGrowthType.yearly,
        unitsOnRent,
        unitsOnFleet,
        null,
        null,
        null,
        null,
        null,
        null,
        null
      );
    }
    return null;
  }

  onCardClicked = (cardType: DashboardCardType) => {
    switch (cardType) {
      case DashboardCardType.ClientRevenue:
        this.chartSwitcher.selectRevenueBookChart();
        break;
      case DashboardCardType.ClientRateVsBenchmark:
        this.chartSwitcher.selectRevenueBenchChart();
        break;
      case DashboardCardType.ClientPhysicalUtilization:
        this.chartSwitcher.selectTotalUtilizationChart();
        break;
      case DashboardCardType.ClientUtilizationVsBenchmark:
        this.chartSwitcher.selectBenchmarkUtilizationChart();
        break;
      case DashboardCardType.ClientDollarUtilization:
        this.chartSwitcher.selectTotalDollarUtilizationChart();
        break;
      case DashboardCardType.ClientDollarUtilizationVsBenchmark:
        this.chartSwitcher.selectBenchmarkDollarUtilizationChart();
        break;
      default:
        break;
    }
  };

  selectBenchmarkUtilizationChartWeeklyChart = () => {
    this.summaryService
      .getWeeklyChartData(
        this.filterProfileService.readClientProfileProperty(
          CODENAMES.CN_SUMMARY_MONTHS_TO_RETURN
        ),
        this.filterService.getCurrentFilter().useRouseSchema
      )
      .subscribe((weeklyData) => {
        this.weeklyUtilization = weeklyData;
        this.chartSwitcher.selectBenchmarkUtilizationWeeklyChart();
      });
  };

  selectTotalBenchmarkUtilizationChartWeeklyChart = () => {
    this.summaryService
      .getWeeklyChartData(
        this.filterProfileService.readClientProfileProperty(
          CODENAMES.CN_SUMMARY_MONTHS_TO_RETURN
        ),
        this.filterService.getCurrentFilter().useRouseSchema
      )
      .subscribe((weeklyData) => {
        this.weeklyUtilization = weeklyData;
        this.chartSwitcher.selectTotalBenchmarkUtilizationWeeklyChart();
      });
  };

  getSelectedChartName() {
    return this.chartSwitcher.translatedCurrent((text) =>
      this.translateService.instant(text)
    );
  }

  getSelectedChartTooltip() {
    switch (this.chartSwitcher.selectedChart) {
      case ChartType.RevenueBook:
        return this.translateService.instant(
          'main.tooltips.summary.client_revenue_vs_book'
        );
      case ChartType.RevenueBench:
        return this.translateService.instant(
          'main.tooltips.summary.client_rate_vs_benchmark'
        );
      case ChartType.TotalRevenueVsBenchmark:
        return this.translateService.instant(
          'main.tooltips.summary.client_revenue_vs_bench_quartiles'
        );
      case ChartType.MoMRateChangeTotal:
        return this.translateService.instant(
          'main.tooltips.summary.client_rate_change_total_mom'
        );
      case ChartType.YoYRateChangeTotal:
        return this.translateService.instant(
          'main.tooltips.summary.client_rate_change_total_yoy'
        );
      case ChartType.MoMRateChange:
        return this.translateService.instant(
          'main.tooltips.summary.client_rate_change_total_vs_bench_mom'
        );
      case ChartType.YoYRateChange:
        return this.translateService.instant(
          'main.tooltips.summary.client_rate_change_total_vs_bench_yoy'
        );
      case ChartType.TotalBenchmarkUtilizationWeekly:
        return this.translateService.instant(
          'main.tooltips.summary.client_physical_utilization_total_weekly'
        );
      case ChartType.TotalUtilization:
        return this.translateService.instant(
          'main.tooltips.summary.client_physical_utilization_total'
        );
      case ChartType.BenchmarkUtilization:
        return this.translateService.instant(
          'main.tooltips.summary.client_physical_utilization_vs_bench'
        );
      case ChartType.BenchmarkUtilizationWeekly:
        return this.translateService.instant(
          'main.tooltips.summary.client_physical_utilization_vs_bench_weekly'
        );
      case ChartType.TotalDollarUtilization:
        return this.translateService.instant(
          'main.tooltips.summary.client_financial_utilization_total'
        );
      case ChartType.BenchmarkDollarUtilization:
        return this.translateService.instant(
          'main.tooltips.summary.client_financial_utilization_vs_bench'
        );
      case ChartType.OnRentInFleetMoM:
        return this.translateService.instant(
          'main.tooltips.summary.client_units_on_rent_growth_vs_bench_mom'
        );
      case ChartType.OnRentInFleetYoY:
        return this.translateService.instant(
          'main.tooltips.summary.client_units_on_rent_growth_vs_bench_yoy'
        );
      case ChartType.RevenueDistributionTotal:
        return this.translateService.instant(
          'main.tooltips.summary.client_business_mix_total'
        );
      case ChartType.RevenueDistributionVsBench:
        return this.translateService.instant(
          'main.tooltips.summary.client_business_mix_vs_bench'
        );
      default:
        return 'none';
    }
  }
}
